/**
 * Argus Live — 3-pane authoring editor route.
 *
 * AL-B3 entry point for `/argus-live/dashboards/:id/edit`.
 *
 * Wire:
 *   - `useArgusLiveDashboard(id)`         current row + version + grant
 *   - `useArgusLiveVersions(id)`          version dropdown source
 *   - `useHasPermission(<action>,         per-action UI gates
 *      'argus_live')`
 *
 * Permission gates (two-layer, per ARGUS-LIVE-SPEC §5.5):
 *   - Layer 1 — `useHasPermission(<action>, 'argus_live')` reads the
 *     tenant role's grant slate (the same matrix the API enforces in
 *     `requirePermission(...)`).
 *   - Layer 2 — `dashboardData.effectiveRole` ('view' / 'edit' / 'admin')
 *     reflects this user's per-dashboard ACL row in
 *     `tenant_dashboard_grants`. Both layers must allow an action for
 *     the corresponding button to be enabled.
 *
 * Read-only outcomes (Layer 2 returns null) still show the editor with
 * the chat composer + Monaco set to read-only — this is intentional so
 * a viewer can copy snippets out. The Save / Publish / Archive buttons
 * are disabled with the same gating helpers as the affordance booleans.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { useArgusLiveDashboard, useArgusLiveVersions } from '@/lib/use-argus-live';
import { useHasPermission } from '@/lib/use-has-permission';
import { ArgusLiveEditor } from '@/components/argus-live';
import type { ArgusLivePermissionFlags } from '@/lib/argus-live-helpers';
import { Link } from '@tanstack/react-router';

export const Route = createFileRoute('/argus-live/dashboards/$id/edit')({
  component: ArgusLiveEditRoute,
});

function ArgusLiveEditRoute() {
  const { id } = Route.useParams();

  const canRead = useHasPermission('read', 'argus_live');
  const canWrite = useHasPermission('write', 'argus_live');
  const canUnlock = useHasPermission('lifecycle:unlock', 'argus_live');
  const canLock = useHasPermission('lifecycle:lock', 'argus_live');
  const canShare = useHasPermission('share', 'argus_live');

  const permissions = useMemo<ArgusLivePermissionFlags>(
    () => ({ canRead, canWrite, canUnlock, canLock, canShare }),
    [canRead, canWrite, canUnlock, canLock, canShare],
  );

  const dashboardQuery = useArgusLiveDashboard(id, { enabled: canRead });
  const versionsQuery = useArgusLiveVersions(canRead ? id : null);

  const [activeVersionId, setActiveVersionId] = useState<string | null>(null);

  // ── Layer 1 hard-gate ────────────────────────────────────────────────
  if (!canRead) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6">
        <AlertCircle className="h-10 w-10 text-amber-500/70 mb-3" />
        <h2 className="text-base font-semibold mb-1">
          You don't have access to Argus Live
        </h2>
        <p className="text-sm text-muted-foreground max-w-md mb-4">
          Argus Live requires the <code className="font-mono">read:argus_live</code>{' '}
          permission. Ask a tenant admin to grant it via the Users module, or
          contact your platform administrator if you believe this is an error.
        </p>
        <Link
          to="/ask-argus"
          className="inline-flex items-center justify-center h-9 px-3 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          Back to Ask Argus
        </Link>
      </div>
    );
  }

  // ── Loading ──────────────────────────────────────────────────────────
  if (dashboardQuery.isLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6">
        <Loader2 className="h-6 w-6 text-primary animate-spin mb-3" />
        <p className="text-xs text-muted-foreground">Loading dashboard…</p>
      </div>
    );
  }

  // ── Error / 404 ──────────────────────────────────────────────────────
  if (dashboardQuery.error || !dashboardQuery.data?.data) {
    const status = (dashboardQuery.error as any)?.status;
    const isNotFound = status === 404;
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6">
        <AlertCircle className="h-10 w-10 text-destructive/70 mb-3" />
        <h2 className="text-base font-semibold mb-1">
          {isNotFound ? 'Dashboard not found' : 'Failed to load dashboard'}
        </h2>
        <p className="text-sm text-muted-foreground max-w-md mb-4">
          {isNotFound
            ? `No dashboard exists with id ${id}, or you don't have a grant on it.`
            : dashboardQuery.error?.message ?? 'Unknown error.'}
        </p>
        <Link
          to="/argus-live"
          className="inline-flex items-center justify-center h-9 px-3 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          Back to dashboards
        </Link>
      </div>
    );
  }

  const dashboardData = dashboardQuery.data.data;
  const versions = versionsQuery.data?.data ?? [];

  return (
    <ArgusLiveEditor
      dashboardData={dashboardData}
      versions={versions}
      permissions={permissions}
      activeVersionId={activeVersionId}
      onChangeActiveVersion={setActiveVersionId}
    />
  );
}
